import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { g } from '~/globals';
import meQuery from '~/queries/meQuery';
import { streamServerEvents } from './serverEvents';

// A connection open at least this long is considered healthy, so the reconnect
// backoff resets after it ends; shorter-lived connections keep backing off.
const STABLE_CONNECTION_MS = 10_000;

/**
 * Holds the realtime SSE connection for the signed-in customer and dispatches
 * incoming events to the React Query cache. Renders nothing.
 */
export const ServerEventsListener = () => {
  const isLoggedIn = useAtomValue(g.reg.auth.isLoggedIn());
  const { data } = useQuery({ ...meQuery(), enabled: isLoggedIn });
  const userId = data?.me?.user?.id;

  useEffect(() => {
    // We open a long-lived SSE stream (fetch + ReadableStream) to receive
    // backend cache-invalidation events. This is an external push channel with
    // no library wrapper in our stack — React Query handles request/response
    // fetches, not server push — so an effect is the right tool.
    //
    // Cleanup aborts the in-flight stream and stops the reconnect loop, on
    // unmount or whenever the signed-in user changes.
    //
    // Deps are [userId] only: the connection is scoped to one customer and must
    // be torn down and reopened when that id changes. g.reg.queryClient and
    // streamServerEvents are module-level singletons, so they need not be listed.
    // Don't open the persistent SSE stream under an automated browser
    // (puppeteer sets navigator.webdriver). A never-closing stream stops
    // puppeteer's `networkidle0` navigations from ever settling, which hangs
    // E2E tests. Realtime isn't exercised by E2E and is unaffected for real
    // users / local dev.
    if (navigator.webdriver) {
      return;
    }
    if (!userId) {
      return;
    }

    const queryClient = g.reg.queryClient;
    const abortController = new AbortController();
    let stopped = false;
    let reconnectDelayMs = 1_000;

    const run = async () => {
      while (!stopped) {
        const startedAt = Date.now();
        try {
          await streamServerEvents(userId, queryClient, abortController.signal);
        } catch {
          // Connection ended (error or server close) — handled by the backoff
          // below. Lossy by design: on reconnect we simply resume.
        }
        if (stopped) {
          return;
        }
        if (Date.now() - startedAt >= STABLE_CONNECTION_MS) {
          reconnectDelayMs = 1_000;
        }
        await new Promise((resolve) => setTimeout(resolve, reconnectDelayMs));
        reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30_000);
      }
    };

    void run();

    return () => {
      stopped = true;
      abortController.abort();
    };
  }, [userId]);

  return null;
};
